import { Flex, Input, InputGroup, InputLeftElement, InputRightElement, Button, forwardRef, Link, Spinner } from "@chakra-ui/react";
import { IoCalendarOutline, IoSearchOutline } from "react-icons/io5";
import { ReactNode, useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";

interface FilterProps {
  loading?: boolean;
  placeholder: string;
  filterTable?: (searchParam: string) => void;
  onSubmitSearch?: (searchParam: string) => void | Promise<void>;
  children?: ReactNode;
  needToSubmit?: boolean;
  setStartDate?: (start: Date | null) => void;
  setEndDate?: (end: Date | null) => void;
  startDate?: Date | null;
  endDate?: Date | null;
  isVisibleDatepicker?: boolean;
}

function Filter({
  filterTable,
  loading,
  placeholder,
  children,
  needToSubmit = false,
  onSubmitSearch,
  setStartDate,
  setEndDate,
  startDate,
  endDate,
  isVisibleDatepicker = false,
}: FilterProps) {
  const [searchParam, setSearchParam] = useState("");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  registerLocale("ptBR", ptBR);

  const onMonthChange = (dates: any) => {
    const [start, end] = dates;
    setStartDate && setStartDate(start);
    setEndDate && setEndDate(end);
  };

  const CustomInput = forwardRef(({ value }: any, ref) => (
    <Button
      onClick={() => setIsDatePickerOpen(true)}
      ref={ref}
      p="5px 12px 5px 20px"
      maxW="220px"
      w="100%"
      fontSize="14px"
      borderRadius="4"
      _hover={{}}
      bgColor="gray.49"
      fontWeight="500"
      //@ts-ignore
      leftIcon={!startDate && !endDate && <IoCalendarOutline color="gray.300" size={20} />}
    >
      {value}
    </Button>
  ));

  const clearFilters = (e: any) => {
    e.preventDefault();
    setSearchParam("");
    if (setStartDate) {
      setStartDate(null);
    }
    if (setEndDate) {
      setEndDate(null);
    }
  };

  useEffect(() => {
    if (isDatePickerOpen) return;
    onSubmitSearch && onSubmitSearch(searchParam);
    // eslint-disable-next-line
  }, [isDatePickerOpen]);

  const handleSearch = async (e: any) => {
    setIsLoading(true);
    e.preventDefault();
    try {
      if (onSubmitSearch) {
        await onSubmitSearch(searchParam);
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Flex
      bg="gray.25"
      borderRadius={8}
      w="100%"
      gap="10"
      align="center"
      p={2}
      as="form"
      onSubmit={handleSearch}
    >
      <InputGroup maxW={500} bg="white">
        <InputLeftElement
          pointerEvents="none"
          children={<IoSearchOutline color="gray.300" size={20} />}
        />
        {needToSubmit ? (
          <Input
            placeholder={placeholder}
            onChange={(e) => setSearchParam(e.currentTarget.value)}
            value={searchParam}
            type="search"
            fontSize="1rem"
          />
        ) : (
          <Input
            placeholder={placeholder}
            onChange={(e) => filterTable && filterTable(e.currentTarget.value)}
            type="search"
            fontSize="1rem"
          />
        )}
        <InputRightElement maxW="70px" w="100%" mr="2">
          {(loading || isLoading) ? (
            <Spinner size="sm" />
          ) : (
            needToSubmit && (
              <Button borderRadius="2px" px={2} maxH="30px" type="submit">
                Buscar
              </Button>
            )
          )}
        </InputRightElement>
      </InputGroup>
      {children}
      {isVisibleDatepicker && (
        <>
          <Flex align="center" gap="2" bgColor="gray.49" borderRadius={8} px="2">
            <DatePicker
              locale="ptBR"
              selected={startDate}
              onChange={(date) => onMonthChange(date)}
              startDate={startDate}
              endDate={endDate}
              onInputClick={() => setIsDatePickerOpen(true)}
              onClickOutside={() => {
                if (!startDate && !endDate) return setIsDatePickerOpen(false);
                !endDate && setEndDate && setEndDate(new Date());
                setIsDatePickerOpen(false);
              }}
              open={isDatePickerOpen}
              customInput={<CustomInput />}
              selectsRange
              dateFormat="dd/MM/yyyy"
              maxDate={new Date()}
            />
          </Flex>
          <Link onClick={clearFilters} color="blue.100">
            Limpar
          </Link>
        </>
      )}
    </Flex>
  );
}

export { Filter };
